import { useState, type ReactNode } from 'react';
import { Button, ErrorState } from '@helios/ui';
import { useSessionScopeFence } from '@/hooks/useSessionScope';
import { useDeviceVoice, useDeviceVoices } from '@/features/ai-chat/useDeviceVoice';
import { AiProvidersPanel } from './AiProvidersPanel';
import { AiProviderConfiguration } from './AiProviderConfiguration';
import { AiAgentControls } from './AiAgentControls';
import { AiKnowledgeSettings } from './AiKnowledgeSettings';
import { AiGuardrailSettings } from './AiGuardrailSettings';
import { useAiAssistantPreferences, useSaveAiPreferences } from './aiSettings.hooks';
import {
  AI_KNOWLEDGE_DOMAINS,
  DEFAULT_AI_PREFERENCES,
  type AiAssistantPreferences,
} from './aiSettings.types';
import { SettingsDraftActions } from './SettingsDraftActions';
import { useSettingsDraft } from './useSettingsDraft';
import {
  SettingsField,
  SettingsLoadingState,
  SettingsMutationBanner,
  SettingsPageFrame,
  SettingsSection,
  SettingsToggle,
  settingsControlClass,
} from './settingsChrome';

const tabs = [
  ['status', 'Status & Models'],
  ['voice', 'Voice & Speech'],
  ['reasoning', 'Reasoning & Actions'],
  ['knowledge', 'Knowledge Base'],
  ['guardrails', 'Guardrails'],
] as const;
type Tab = (typeof tabs)[number][0];
export function AiSettingsPage() {
  const [tab, setTab] = useState<Tab>('status');
  const query = useAiAssistantPreferences();
  const save = useSaveAiPreferences();
  const fence = useSessionScopeFence();
  const draft = useSettingsDraft(
    query.data ? { form: query.data.preferences, version: query.data.version } : null,
  );
  const [notice, setNotice] = useState<{ scope: string; message: string } | null>(null);
  const form = draft.form;
  const change = <K extends keyof AiAssistantPreferences>(
    key: K,
    value: AiAssistantPreferences[K],
  ) => {
    if (form) draft.change({ ...form, [key]: value });
  };
  const savePreferences = async () => {
    if (!fence.enabled || save.isPending || !draft.dirty || !form || draft.version === null) return;
    const origin = fence.identity;
    try {
      const saved = await save.mutateAsync({ expectedVersion: draft.version, preferences: form });
      if (!fence.isCurrent(origin)) return;
      draft.accept({ form: saved.preferences, version: saved.version });
      setNotice({
        scope: draft.scope,
        message: 'Your AI preferences are saved and apply to Helios.',
      });
    } catch {
      /* Keep unsaved edits; the session-scoped banner reports the error. */
    }
  };
  const preferenceContent = (children: ReactNode) =>
    query.isLoading ? (
      <SettingsLoadingState label="Loading AI preferences…" />
    ) : !form || !query.data ? (
      <ErrorState
        title="AI preferences unavailable"
        description={
          query.error instanceof Error
            ? query.error.message
            : 'Your AI preferences could not be loaded.'
        }
        action={
          <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
            Retry preferences
          </Button>
        }
      />
    ) : (
      children
    );
  return (
    <SettingsPageFrame
      eyebrow="Workspace"
      title="AI assistant"
      description="Provider connections, personal voice and reasoning preferences, advisor agents, knowledge, and safeguards."
      actions={
        <Button
          size="xs"
          variant="outline"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Refresh preferences
        </Button>
      }
    >
      <nav
        className="border-border bg-card flex flex-wrap gap-1 rounded-lg border p-2"
        aria-label="AI settings sections"
      >
        {tabs.map(([value, label]) => (
          <Button
            key={value}
            size="xs"
            type="button"
            variant={tab === value ? 'default' : 'outline'}
            aria-pressed={tab === value}
            onClick={() => setTab(value)}
          >
            {label}
          </Button>
        ))}
      </nav>
      {/* Keep forms mounted so section changes preserve unsaved drafts. */}
      <div hidden={tab !== 'status'}>
        <div className="grid gap-2.5">
          <AiProviderConfiguration />
          <AiProvidersPanel />
        </div>
      </div>
      <div hidden={tab !== 'voice'}>
        {preferenceContent(
          <VoicePreferences
            form={form ?? DEFAULT_AI_PREFERENCES}
            change={change}
            disabled={!form || save.isPending}
            enabled={fence.enabled && tab === 'voice'}
          />,
        )}
      </div>
      <div hidden={tab !== 'reasoning'}>
        <div className="grid gap-2.5">
          {preferenceContent(
            <SettingsSection
              title="Reasoning & actions"
              description="Personal response preferences and the approval behavior of the Helios assistant."
            >
              <div className="grid gap-2.5">
                <div className="grid gap-2 sm:grid-cols-3">
                  <SettingsField label="Response style">
                    <select
                      className={settingsControlClass}
                      disabled={!form || save.isPending}
                      value={form?.responseStyle ?? 'balanced'}
                      onChange={(event) =>
                        change(
                          'responseStyle',
                          event.target.value as AiAssistantPreferences['responseStyle'],
                        )
                      }
                    >
                      <option value="focused">Focused · concise and practical</option>
                      <option value="balanced">Balanced · context and next steps</option>
                      <option value="creative">Creative · explore alternatives</option>
                    </select>
                  </SettingsField>
                  <SettingsField
                    label={`Creativity · saved ${(form?.temperature ?? 0.3).toFixed(2)} (inactive)`}
                  >
                    <input
                      type="range"
                      className="accent-primary h-7 w-full"
                      min={0}
                      max={1}
                      step={0.05}
                      disabled
                      aria-describedby="ai-creativity-inactive-help"
                      value={form?.temperature ?? 0.3}
                    />
                  </SettingsField>
                  <SettingsField label="Assistant action mode">
                    <select
                      className={settingsControlClass}
                      disabled={!form || save.isPending}
                      value={form?.actionMode ?? 'approval'}
                      onChange={(event) =>
                        change(
                          'actionMode',
                          event.target.value as AiAssistantPreferences['actionMode'],
                        )
                      }
                    >
                      <option value="draft_only">
                        Draft only · do not change business records
                      </option>
                      <option value="approval">Prepare actions for required approval</option>
                    </select>
                  </SettingsField>
                </div>
                <SettingsToggle
                  label="Show advisor alerts in Helios"
                  checked={form?.opportunityAlerts ?? true}
                  disabled={!form || save.isPending}
                  onChange={(value) => change('opportunityAlerts', value)}
                />
                <p id="ai-creativity-inactive-help" className="text-muted-foreground text-2xs">
                  Creativity is inactive for the current Claude connection. Your saved value is
                  kept. Use Response style to guide the wording. Required approvals and permissions
                  always apply. Draft-only also blocks execution of queued actions.
                </p>
              </div>
            </SettingsSection>,
          )}
          <AiAgentControls />
        </div>
      </div>
      <div hidden={tab !== 'knowledge'}>
        <div className="grid gap-2.5">
          {preferenceContent(
            <SettingsSection
              title="Workspace context sources"
              description="Choose the domains Helios may load automatically. Your access permissions still apply."
            >
              <div className="grid gap-2 sm:grid-cols-3">
                {AI_KNOWLEDGE_DOMAINS.map((domain) => (
                  <SettingsToggle
                    key={domain}
                    label={
                      {
                        projects: 'Projects',
                        finance: 'Finance',
                        procurement: 'Procurement',
                        workforce: 'Workforce',
                        safety: 'Safety',
                        documents: 'Documents & reference library',
                      }[domain]
                    }
                    checked={form?.knowledgeSources[domain] ?? true}
                    disabled={!form || save.isPending}
                    onChange={(value) =>
                      form &&
                      change('knowledgeSources', { ...form.knowledgeSources, [domain]: value })
                    }
                  />
                ))}
              </div>
            </SettingsSection>,
          )}
          <AiKnowledgeSettings />
        </div>
      </div>
      <div hidden={tab !== 'guardrails'}>
        <AiGuardrailSettings />
      </div>
      <SettingsSection
        title="Apply personal AI preferences"
        description="Voice, reasoning, alerts, and context choices apply to your account. Provider keys and workspace policies have their own save controls."
      >
        <div className="grid gap-2">
          <SettingsMutationBanner
            message={notice?.scope === draft.scope ? notice.message : null}
            error={save.error}
          />
          <SettingsDraftActions
            dirty={draft.dirty}
            changedOnServer={draft.changedOnServer}
            disabled={save.isPending}
            onRevert={() => {
              draft.accept();
              save.reset();
              setNotice(null);
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="xs"
              disabled={!form || !query.data || !draft.dirty || save.isPending}
              onClick={() => void savePreferences()}
            >
              {save.isPending ? 'Saving…' : 'Save AI preferences'}
            </Button>
            <span className="text-muted-foreground text-2xs">
              {draft.dirty
                ? 'Unsaved personal preferences'
                : query.data
                  ? 'All personal preferences saved'
                  : 'Preferences unavailable'}
            </span>
          </div>
        </div>
      </SettingsSection>
    </SettingsPageFrame>
  );
}

function VoicePreferences({
  form,
  change,
  disabled,
  enabled,
}: {
  form: AiAssistantPreferences;
  change: <K extends keyof AiAssistantPreferences>(
    key: K,
    value: AiAssistantPreferences[K],
  ) => void;
  disabled: boolean;
  enabled: boolean;
}) {
  const voices = useDeviceVoices();
  const device = useDeviceVoice({ preferences: form, enabled });
  const voiceMissing = Boolean(
    form.assistantVoice && !voices.some((voice) => voice.voiceURI === form.assistantVoice),
  );
  return (
    <SettingsSection
      title="Assistant voice & speech"
      description="Device voices handle previews and spoken replies. Recorded audio and generated AI speech use the OpenAI connection."
    >
      <div className="grid gap-3">
        <div className="grid gap-2 sm:grid-cols-2">
          <SettingsToggle
            label="Spoken replies"
            checked={form.voiceOutputEnabled}
            disabled={disabled}
            onChange={(value) => change('voiceOutputEnabled', value)}
          />
          <SettingsToggle
            label="Announce page and selection changes"
            checked={form.proactiveNarration}
            disabled={disabled}
            onChange={(value) => change('proactiveNarration', value)}
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          <SettingsField label="Device assistant voice">
            <select
              className={settingsControlClass}
              disabled={disabled || !device.speechSupported}
              value={form.assistantVoice}
              onChange={(event) => change('assistantVoice', event.target.value)}
            >
              <option value="">Device default voice</option>
              {voiceMissing ? (
                <option value={form.assistantVoice}>Saved voice unavailable on this device</option>
              ) : null}
              {voices.map((voice) => (
                <option key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.name} · {voice.lang}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Generated AI voice">
            <select
              className={settingsControlClass}
              disabled={disabled}
              value={form.aiVoice}
              onChange={(event) => change('aiVoice', event.target.value)}
            >
              {[
                'alloy',
                'ash',
                'ballad',
                'coral',
                'echo',
                'fable',
                'nova',
                'onyx',
                'sage',
                'shimmer',
                'verse',
                'marin',
                'cedar',
              ].map((voice) => (
                <option key={voice} value={voice}>
                  {voice[0].toUpperCase() + voice.slice(1)}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label="Recognition language">
            <select
              className={settingsControlClass}
              disabled={disabled}
              value={form.voiceLanguage}
              onChange={(event) => change('voiceLanguage', event.target.value)}
            >
              {[
                ['en-US', 'English (United States)'],
                ['en-GB', 'English (United Kingdom)'],
                ['en-CA', 'English (Canada)'],
                ['es-US', 'Spanish (United States)'],
                ['es-MX', 'Spanish (Mexico)'],
                ['fr-CA', 'French (Canada)'],
              ].map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </SettingsField>
          <SettingsField label={`Speaking rate · ${form.speechRate.toFixed(1)}×`}>
            <input
              className="accent-primary h-7 w-full"
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              disabled={disabled}
              value={form.speechRate}
              onChange={(event) => change('speechRate', Number(event.target.value))}
            />
          </SettingsField>
          <SettingsField label={`Pitch · ${form.voicePitch.toFixed(1)}`}>
            <input
              className="accent-primary h-7 w-full"
              type="range"
              min={0.5}
              max={2}
              step={0.1}
              disabled={disabled}
              value={form.voicePitch}
              onChange={(event) => change('voicePitch', Number(event.target.value))}
            />
          </SettingsField>
          <SettingsField label={`Volume · ${Math.round(form.voiceVolume * 100)}%`}>
            <input
              className="accent-primary h-7 w-full"
              type="range"
              min={0}
              max={1}
              step={0.05}
              disabled={disabled}
              value={form.voiceVolume}
              onChange={(event) => change('voiceVolume', Number(event.target.value))}
            />
          </SettingsField>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="xs"
            disabled={disabled || !device.speechSupported}
            onClick={() =>
              device.speak(
                'Hello. I am Helios, your construction operations assistant. This is your selected voice.',
                { manual: true },
              )
            }
          >
            Preview device voice
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            disabled={!device.speechSupported}
            onClick={device.stopSpeaking}
          >
            Stop preview
          </Button>
          <span className="text-muted-foreground text-2xs">
            Device playback has no platform provider charge.
          </span>
        </div>
        {device.error ? (
          <p className="text-warning text-xs" role="status">
            {device.error}
          </p>
        ) : null}
        {!device.speechSupported ? (
          <p className="text-muted-foreground text-xs">
            Device speech is unavailable in this browser. Generated speech requires an OpenAI
            connection.
          </p>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-2">
          <SettingsToggle
            label="Continuous listening after microphone start"
            checked={form.continuousListening}
            disabled={disabled}
            onChange={(value) => change('continuousListening', value)}
          />
          <SettingsToggle
            label="Send final recognized speech automatically"
            checked={form.voiceAutoSend}
            disabled={disabled}
            onChange={(value) => change('voiceAutoSend', value)}
          />
        </div>
        <p className="text-muted-foreground text-2xs">
          Listening starts when you click the microphone in Helios. Browser permission is required.
          When auto-send is off, recognized words stay in the composer for review. Browser
          recognition may use a browser-managed service.
        </p>
      </div>
    </SettingsSection>
  );
}
